import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Save, Trash2, X } from 'lucide-react';

import ActionButton from '../../../../../components/Buttons/ActionButton';
import FormModal from '../../../../../components/Modals/FormModal';
import InputField from '../../../../../components/Forms/Input/InputField';
import SelectField from '../../../../../components/Forms/Input/SelectField';
import TextAreaField from '../../../../../components/Forms/Input/TextAreaField';
import ToggleSwitch from '../../../../../components/Forms/Input/ToggleSwitch';

import useAlerts from '../../../../../hooks/useAlerts';
import useApiErrorHandler from '../../../../../hooks/useApiErrorHandler';
import RestaurantCatalogExtensionService from '../../../../../services/restaurant/RestaurantCatalogExtensionService';
import {
  RestaurantModifierDTO,
  RestaurantModifierGroupDTO,
} from '../../../../../types/restaurant/RestaurantCatalogExtensionTypes';
import {
  RawMaterialUnitOption,
  RestaurantRawMaterialDTO,
} from '../../../../../types/restaurant/RestaurantRawMaterialTypes';

interface Props {
  open: boolean;
  branchId: number;
  group: RestaurantModifierGroupDTO | null;
  rawMaterials: RestaurantRawMaterialDTO[];
  units: RawMaterialUnitOption[];
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_GROUP = {
  name: '',
  description: '',
  minSelections: 0,
  maxSelections: 1,
  required: false,
  displayOrder: 1,
  active: true,
};

interface OptionDraft {
  id?: number;
  name: string;
  price: string;
  deduct: boolean;
  rawMaterialId: number | '';
  quantity: string;
  unitId: number | '';
}

const EMPTY_OPTION: OptionDraft = {
  name: '',
  price: '',
  deduct: false,
  rawMaterialId: '',
  quantity: '',
  unitId: '',
};

const modifierToDraft = (m: RestaurantModifierDTO): OptionDraft => ({
  id: m.id,
  name: m.name,
  price: String(m.priceAdjustment ?? 0),
  deduct: m.rawMaterialId != null,
  rawMaterialId: m.rawMaterialId ?? '',
  quantity: m.consumptionQuantity != null ? String(m.consumptionQuantity) : '',
  unitId: m.consumptionUnitId ?? '',
});

const ModifierGroupModal = ({
  open,
  branchId,
  group,
  rawMaterials,
  units,
  onClose,
  onSaved,
}: Props) => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();

  const [form, setForm] = useState(EMPTY_GROUP);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [options, setOptions] = useState<RestaurantModifierDTO[]>([]);
  const [savingGroup, setSavingGroup] = useState(false);
  const [dirtyGroup, setDirtyGroup] = useState(false);

  const [optionDraft, setOptionDraft] = useState<OptionDraft>(EMPTY_OPTION);
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [savingOption, setSavingOption] = useState(false);
  const [busyOptionId, setBusyOptionId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    if (group) {
      setForm({
        name: group.name,
        description: group.description ?? '',
        minSelections: group.minSelections,
        maxSelections: group.maxSelections,
        required: group.required,
        displayOrder: group.displayOrder,
        active: group.active,
      });
      setGroupId(group.id);
      setOptions(group.modifiers);
    } else {
      setForm(EMPTY_GROUP);
      setGroupId(null);
      setOptions([]);
    }
    setDirtyGroup(false);
    setShowOptionForm(false);
    setOptionDraft(EMPTY_OPTION);
    setConfirmDeleteId(null);
  }, [open, group]);

  const patchForm = <K extends keyof typeof EMPTY_GROUP>(
    key: K,
    value: (typeof EMPTY_GROUP)[K]
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirtyGroup(true);
  };

  const rawMaterialOptions = useMemo(
    () => [
      { value: 0, label: 'Selecciona materia prima' },
      ...rawMaterials.map((rm) => ({
        value: rm.id,
        label: `${rm.code} · ${rm.name}`,
      })),
    ],
    [rawMaterials]
  );
  const unitOptions = useMemo(
    () => [
      { value: 0, label: 'Unidad' },
      ...units.map((u) => ({ value: u.id, label: u.name })),
    ],
    [units]
  );

  const saveGroup = async () => {
    const name = form.name.trim();
    if (!name) {
      show('error', 'Falta el nombre', 'Escribe un nombre para el grupo.');
      return;
    }
    const min = Math.max(0, Number(form.minSelections) || 0);
    const max = Math.max(1, Number(form.maxSelections) || 1);
    if (min > max) {
      show('error', 'Rango inválido', 'El mínimo no puede superar al máximo.');
      return;
    }
    setSavingGroup(true);
    try {
      const body = {
        name,
        description: form.description.trim() || null,
        minSelections: form.required && min < 1 ? 1 : min,
        maxSelections: max,
        required: form.required,
        displayOrder: Math.max(0, Number(form.displayOrder) || 0),
        active: form.active,
      };
      const response = groupId
        ? await RestaurantCatalogExtensionService.updateModifierGroup(
            branchId,
            groupId,
            body
          )
        : await RestaurantCatalogExtensionService.createModifierGroup(
            branchId,
            body
          );
      setGroupId(response.data.id);
      setOptions(response.data.modifiers ?? options);
      setDirtyGroup(false);
      onSaved();
      show(
        'success',
        'Grupo guardado',
        groupId ? 'El grupo se actualizó.' : 'Ahora puedes agregar sus opciones.'
      );
    } catch (error) {
      handle(error);
    } finally {
      setSavingGroup(false);
    }
  };

  const openOptionEditor = (option?: RestaurantModifierDTO) => {
    setOptionDraft(option ? modifierToDraft(option) : EMPTY_OPTION);
    setShowOptionForm(true);
  };

  const saveOption = async () => {
    if (!groupId) return;
    const name = optionDraft.name.trim();
    if (!name) {
      show('error', 'Falta el nombre', 'Escribe un nombre para la opción.');
      return;
    }
    if (
      optionDraft.deduct &&
      (!optionDraft.rawMaterialId ||
        !optionDraft.quantity ||
        !optionDraft.unitId)
    ) {
      show(
        'error',
        'Datos de consumo incompletos',
        'Indica materia prima, cantidad y unidad.'
      );
      return;
    }
    setSavingOption(true);
    try {
      const body = {
        groupId,
        name,
        priceAdjustment: Number(optionDraft.price) || 0,
        rawMaterialId: optionDraft.deduct
          ? Number(optionDraft.rawMaterialId)
          : null,
        consumptionQuantity: optionDraft.deduct
          ? Number(optionDraft.quantity)
          : null,
        consumptionUnitId: optionDraft.deduct
          ? Number(optionDraft.unitId)
          : null,
        displayOrder: optionDraft.id
          ? options.findIndex((o) => o.id === optionDraft.id) + 1
          : options.length + 1,
        active: true,
      };
      const response = optionDraft.id
        ? await RestaurantCatalogExtensionService.updateModifier(
            branchId,
            optionDraft.id,
            body
          )
        : await RestaurantCatalogExtensionService.createModifier(branchId, body);
      setOptions((current) =>
        optionDraft.id
          ? current.map((o) => (o.id === optionDraft.id ? response.data : o))
          : [...current, response.data]
      );
      setShowOptionForm(false);
      setOptionDraft(EMPTY_OPTION);
      onSaved();
    } catch (error) {
      handle(error);
    } finally {
      setSavingOption(false);
    }
  };

  const deleteOption = async (id: number) => {
    setBusyOptionId(id);
    setConfirmDeleteId(null);
    try {
      await RestaurantCatalogExtensionService.deleteModifier(branchId, id);
      setOptions((current) => current.filter((o) => o.id !== id));
      onSaved();
    } catch (error) {
      handle(error);
    } finally {
      setBusyOptionId(null);
    }
  };

  return (
    <FormModal
      isOpen={open}
      onClose={savingGroup ? undefined : onClose}
      title={groupId ? `Editar grupo · ${form.name}` : 'Nuevo grupo de modificadores'}
      maxWidth="max-w-2xl"
      zIndex="top"
    >
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <InputField
              label="Nombre del grupo"
              name="name"
              type="text"
              iconKey="tag"
              value={form.name}
              disabled={savingGroup}
              placeholder="Ej. Punto de cocción"
              onChange={(e) => patchForm('name', e.target.value)}
            />
          </div>
          <InputField
            label="Mín. selecciones"
            name="min"
            type="number"
            min={0}
            value={form.minSelections}
            disabled={savingGroup}
            onChange={(e) =>
              patchForm('minSelections', Math.max(0, Number(e.target.value)))
            }
          />
          <InputField
            label="Máx. selecciones"
            name="max"
            type="number"
            min={1}
            value={form.maxSelections}
            disabled={savingGroup}
            onChange={(e) =>
              patchForm('maxSelections', Math.max(1, Number(e.target.value)))
            }
          />
          <InputField
            label="Orden"
            name="order"
            type="number"
            min={0}
            value={form.displayOrder}
            disabled={savingGroup}
            onChange={(e) =>
              patchForm('displayOrder', Math.max(0, Number(e.target.value)))
            }
          />
          <div className="flex items-center gap-6 pt-6">
            <ToggleSwitch
              label="Obligatorio"
              name="required"
              checked={form.required}
              disabled={savingGroup}
              onChange={() => patchForm('required', !form.required)}
            />
            <ToggleSwitch
              label="Activo"
              name="active"
              checked={form.active}
              disabled={savingGroup}
              onChange={() => patchForm('active', !form.active)}
            />
          </div>
          <div className="sm:col-span-2">
            <TextAreaField
              label="Descripción"
              name="description"
              rows={2}
              value={form.description}
              disabled={savingGroup}
              placeholder="Opcional"
              onChange={(e) => patchForm('description', e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <ActionButton
            label={
              savingGroup
                ? 'Guardando…'
                : groupId
                ? 'Guardar grupo'
                : 'Crear grupo'
            }
            variant="primary"
            size="sm"
            fullWidth={false}
            disabled={savingGroup || (!!groupId && !dirtyGroup)}
            icon={
              savingGroup ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )
            }
            onClick={saveGroup}
          />
        </div>

        {groupId && (
          <div className="border-t border-stroke pt-4 dark:border-strokedark">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-black dark:text-white">
                Opciones ({options.length})
              </p>
              {!showOptionForm && (
                <button
                  type="button"
                  onClick={() => openOptionEditor()}
                  className="inline-flex items-center gap-1 rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Nueva opción
                </button>
              )}
            </div>

            <div className="space-y-1.5">
              {options.length === 0 && !showOptionForm && (
                <p className="rounded-lg border border-dashed border-stroke px-3 py-4 text-center text-xs text-bodydark2 dark:border-strokedark">
                  Sin opciones todavía.
                </p>
              )}

              {options.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center gap-2 rounded-lg border border-stroke px-3 py-2 dark:border-strokedark"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-black dark:text-white">
                    {option.name}
                    {Number(option.priceAdjustment) !== 0 && (
                      <span className="ml-1 text-xs text-bodydark2">
                        ({Number(option.priceAdjustment) > 0 ? '+' : ''}
                        {option.priceAdjustment})
                      </span>
                    )}
                    {option.rawMaterialId != null && (
                      <span className="ml-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                        MP
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => openOptionEditor(option)}
                    className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5"
                  >
                    Editar
                  </button>
                  {busyOptionId === option.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-danger" />
                  ) : confirmDeleteId === option.id ? (
                    <button
                      type="button"
                      onClick={() => deleteOption(option.id)}
                      onBlur={() => setConfirmDeleteId(null)}
                      className="rounded bg-danger/10 px-2 py-1 text-[10px] font-semibold text-danger"
                    >
                      ¿Eliminar?
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(option.id)}
                      aria-label={`Eliminar ${option.name}`}
                      className="rounded-md p-1.5 text-bodydark2 hover:bg-danger/10 hover:text-danger"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {showOptionForm && (
              <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-black dark:text-white">
                    {optionDraft.id ? 'Editar opción' : 'Nueva opción'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowOptionForm(false)}
                    className="text-bodydark2 hover:text-danger"
                    aria-label="Cerrar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <InputField
                    label="Nombre"
                    name="optName"
                    type="text"
                    value={optionDraft.name}
                    onChange={(e) =>
                      setOptionDraft((c) => ({ ...c, name: e.target.value }))
                    }
                  />
                  <InputField
                    label="Ajuste de precio"
                    name="optPrice"
                    type="number"
                    step="0.01"
                    value={optionDraft.price}
                    placeholder="0"
                    onChange={(e) =>
                      setOptionDraft((c) => ({ ...c, price: e.target.value }))
                    }
                  />
                  <div className="sm:col-span-2">
                    <ToggleSwitch
                      label="Descuenta materia prima al vender"
                      name="optDeduct"
                      checked={optionDraft.deduct}
                      onChange={() =>
                        setOptionDraft((c) => ({ ...c, deduct: !c.deduct }))
                      }
                    />
                  </div>

                  {optionDraft.deduct && (
                    <>
                      <div className="sm:col-span-2">
                        <SelectField
                          label="Materia prima"
                          name="optRm"
                          value={optionDraft.rawMaterialId || 0}
                          options={rawMaterialOptions}
                          onChange={(e) =>
                            setOptionDraft((c) => ({
                              ...c,
                              rawMaterialId: Number(e.target.value) || '',
                            }))
                          }
                        />
                      </div>
                      <InputField
                        label="Cantidad"
                        name="optQty"
                        type="number"
                        step="0.0001"
                        value={optionDraft.quantity}
                        onChange={(e) =>
                          setOptionDraft((c) => ({
                            ...c,
                            quantity: e.target.value,
                          }))
                        }
                      />
                      <SelectField
                        label="Unidad"
                        name="optUnit"
                        value={optionDraft.unitId || 0}
                        options={unitOptions}
                        onChange={(e) =>
                          setOptionDraft((c) => ({
                            ...c,
                            unitId: Number(e.target.value) || '',
                          }))
                        }
                      />
                    </>
                  )}
                </div>

                <div className="mt-3 flex justify-end">
                  <ActionButton
                    label={savingOption ? 'Guardando…' : 'Guardar opción'}
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    disabled={savingOption}
                    icon={
                      savingOption ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )
                    }
                    onClick={saveOption}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end border-t border-stroke pt-4 dark:border-strokedark">
          <ActionButton
            label="Cerrar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={savingGroup}
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          />
        </div>
      </div>
    </FormModal>
  );
};

export default ModifierGroupModal;
